/**
 * Utility functions for handling logo images during registration.
 * All external URL fetching is done via backend proxy for security.
 *
 * Note: errorMessage values are translation keys - use translate() in components.
 */

import appStorage from 'storage/appStorage'
import { osFetch } from 'util/fetch'

export type LogoFetchErrorCode =
  | 'INVALID_URL'
  | 'FETCH_FAILED'
  | 'TIMEOUT'
  | 'INVALID_CONTENT_TYPE'
  | 'FILE_TOO_LARGE'
  | 'BLOCKED'
  | 'SVG_NOT_ALLOWED'
  | 'INVALID_IMAGE'
  | 'NETWORK_ERROR'
  | 'AUTH_REQUIRED'
  | 'UNKNOWN_ERROR'

export interface LogoFetchResult {
  success: boolean
  data?: { src: string; title: string }
  errorCode?: LogoFetchErrorCode
  errorMessage?: string
}

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_URL: 'Invalid image URL provided',
  BLOCKED: 'This URL is not allowed',
  SVG_NOT_ALLOWED: 'SVG images are not supported for security reasons',
  INVALID_IMAGE: 'The file is not a valid image',
  FILE_TOO_LARGE: 'Logo image is too large (max 5MB)',
  INVALID_CONTENT_TYPE: 'The URL does not point to a valid image',
  FETCH_FAILED: 'Failed to fetch the logo image',
  TIMEOUT: 'Logo fetch timed out - the image server may be slow',
  AUTH_REQUIRED: 'Authentication required to import logo',
  NETWORK_ERROR: 'Network error while importing logo. Please try again.',
  UNKNOWN_ERROR: 'Failed to import logo',
}

/**
 * Fetch an external image URL via backend proxy.
 * All security validation (URL, content-type, magic bytes, size) is done on backend.
 *
 */
export const fetchImageAsBase64WithDetails = async (imageUrl: string): Promise<LogoFetchResult> => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return { success: false, errorCode: 'INVALID_URL', errorMessage: ERROR_MESSAGES.INVALID_URL }
  }

  try {
    new URL(imageUrl)
  } catch {
    return { success: false, errorCode: 'INVALID_URL', errorMessage: ERROR_MESSAGES.INVALID_URL }
  }

  try {
    const token = appStorage.getToken()

    if (!token) {
      return {
        success: false,
        errorCode: 'AUTH_REQUIRED',
        errorMessage: ERROR_MESSAGES.AUTH_REQUIRED,
      }
    }

    const response = await osFetch(`${window.API_ROOT}/api/ada/registration/fetch-image/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ url: imageUrl }),
    })

    const data = await response.json()

    if (data.success && data.data) {
      return {
        success: true,
        data: data.data,
      }
    }

    const errorCode = data.error_code || 'FETCH_FAILED'
    return {
      success: false,
      errorCode: errorCode as LogoFetchErrorCode,
      errorMessage: ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.UNKNOWN_ERROR,
    }
  } catch (error) {
    console.warn('[logoUtils] Backend proxy failed:', error)
    return {
      success: false,
      errorCode: 'NETWORK_ERROR',
      errorMessage: ERROR_MESSAGES.NETWORK_ERROR,
    }
  }
}

/**
 * Fetch an external image URL and convert it to base64 format
 * Returns an object suitable for the backend's logo_file_contents field
 *
 */
export const fetchImageAsBase64ForUpload = async (imageUrl: string): Promise<{ src: string; title: string } | null> => {
  const result = await fetchImageAsBase64WithDetails(imageUrl)
  if (result.success && result.data) {
    return result.data
  }
  if (result.errorCode) {
    console.warn(`[logoUtils] Logo fetch failed: ${result.errorCode} - ${result.errorMessage}`)
  }
  return null
}

/**
 * Fetch an external image URL and convert it to a JSON string format
 * suitable for the registration API's logo field
 */
export const fetchImageAsBase64String = async (imageUrl: string): Promise<string | null> => {
  const logoData = await fetchImageAsBase64ForUpload(imageUrl)
  if (logoData) {
    return JSON.stringify(logoData)
  }
  return null
}

/**
 * Convert a File object to base64 format for upload
 */
export const fileToBase64ForUpload = async (file: File): Promise<{ src: string; title: string }> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      resolve({
        src: reader.result as string,
        title: file.name,
      })
    }
    reader.readAsDataURL(file)
  })
}
